import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Your companions — moved here from the homepage. Sort by recent activity or
// a–z; the + in the top right adds a new one (admin only).

const PlusIcon = ({ size = 16, color = '#0e0b0f' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 5v14" />
    <path d="M5 12h14" />
  </svg>
);

export default function CompanionList() {
  const [companions, setCompanions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortMode, setSortMode] = useState(
    () => localStorage.getItem('companionSort') || 'activity'
  );
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const isAdmin = localStorage.getItem('is_admin') === 'true';

  useEffect(() => {
    if (!token) { navigate('/'); return; }
    axios.get(`${API}/companions?token=${token}`)
      .then(res => setCompanions(res.data.companions))
      .catch(err => console.log(err))
      .finally(() => setLoading(false));
  }, []);

  const changeSort = (mode) => {
    setSortMode(mode);
    localStorage.setItem('companionSort', mode);
  };

  /* activity order comes pre-sorted from the backend, so we leave it alone.
     a-z is a pure client-side reorder — no extra request. */
  const sorted = useMemo(() => {
    if (sortMode === 'alpha') {
      return [...companions].sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
      );
    }
    return companions;
  }, [companions, sortMode]);

  const s = {
    page: {
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: '#0e0b0f',
      fontFamily: "'DM Sans', sans-serif",
      fontWeight: 300,
      color: '#e8ddd0',
      display: 'flex', flexDirection: 'column',
      overflowY: 'hidden',
    },
    header: {
      padding: '20px 28px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '12px',
      borderBottom: '1px solid rgba(201,147,90,0.06)',
      background: 'linear-gradient(180deg, #120f18 0%, #0e0b0f 100%)',
      flexShrink: 0,
      zIndex: 50,
    },
    backBtn: {
      background: 'transparent', border: 'none', cursor: 'pointer',
      color: '#4a3d30', fontSize: '13px', fontFamily: "'DM Sans', sans-serif",
      flexShrink: 0, minWidth: '60px', textAlign: 'left', padding: 0,
    },
    title: {
      fontFamily: "'Cormorant Garamond', serif",
      fontSize: '20px', fontWeight: 300, fontStyle: 'italic',
      color: '#e8ddd0', letterSpacing: '0.04em',
    },
    headerRight: { minWidth: '60px', display: 'flex', justifyContent: 'flex-end' },
    plusBtn: {
      width: '34px', height: '34px', borderRadius: '50%', border: 'none',
      background: 'linear-gradient(135deg, #8a5f35, #c9935a)',
      cursor: 'pointer', display: 'inline-flex', alignItems: 'center',
      justifyContent: 'center', flexShrink: 0,
    },
    body: {
      flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch',
      padding: '28px 24px 80px', maxWidth: '680px', width: '100%',
      margin: '0 auto', boxSizing: 'border-box',
    },
    sectionTitle: {
      fontSize: '11px', letterSpacing: '0.16em', color: '#4a3d30',
      textTransform: 'uppercase',
    },
    sectionRow: {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      marginBottom: '16px', gap: '12px',
    },
    sortWrap: { display: 'flex', alignItems: 'center', gap: '2px', flexShrink: 0 },
    sortBtn: (active) => ({
      border: 'none',
      padding: '5px 10px', borderRadius: '14px',
      fontSize: '11px', letterSpacing: '0.08em', textTransform: 'uppercase',
      fontFamily: "'DM Sans', sans-serif", fontWeight: 300,
      color: active ? '#c9935a' : '#4a3d30',
      background: active ? 'rgba(201,147,90,0.08)' : 'transparent',
      cursor: 'pointer',
    }),
    companionGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr 1fr',
      gap: '8px',
    },
    companionCard: (isFirst) => ({
      background: '#1e1825',
      border: isFirst ? '1px solid rgba(201,147,90,0.2)' : '1px solid rgba(201,147,90,0.06)',
      borderRadius: '16px',
      padding: '18px 8px 16px',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      cursor: 'pointer',
      position: 'relative', overflow: 'hidden',
      boxSizing: 'border-box',
      WebkitUserSelect: 'none', userSelect: 'none',
    }),
    companionGlow: {
      position: 'absolute', left: 0, top: 0, right: 0, height: '3px',
      background: 'linear-gradient(90deg, #8a5f35, #c9935a)',
    },
    companionAvatar: {
      width: '56px', height: '56px', borderRadius: '50%',
      background: 'linear-gradient(135deg, #3a2a1a, #5a3d20)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '21px', color: '#c9935a',
      fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic',
      flexShrink: 0, overflow: 'hidden',
      boxShadow: '0 0 0 2px rgba(201,147,90,0.1)',
      marginBottom: '10px',
    },
    companionImg: { width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' },
    companionName: {
      fontFamily: "'Cormorant Garamond', serif",
      fontSize: '16px', fontWeight: 300, color: '#e8ddd0',
      textAlign: 'center', lineHeight: 1.2,
      maxWidth: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
    },
    emptyState: {
      background: '#1e1825', border: '1px dashed rgba(201,147,90,0.15)',
      borderRadius: '18px', padding: '36px 24px',
      textAlign: 'center',
    },
    emptyText: { fontSize: '14px', color: '#4a3d30', lineHeight: 1.8 },
  };

  return (
    <div style={s.page}>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=DM+Sans:wght@300;400&display=swap" rel="stylesheet" />

      <style>{`
        .r-card { transition: border-color .18s ease, transform .12s ease; }
        .r-card:hover { border-color: rgba(201,147,90,0.32); }
        .r-card:active { transform: scale(0.985); }
        .r-card:focus-visible { outline: 2px solid #c9935a; outline-offset: 2px; }
        @media (prefers-reduced-motion: reduce) {
          .r-card { transition: none; }
          .r-card:active { transform: none; }
        }
      `}</style>

      {/* HEADER */}
      <header style={s.header}>
        <button style={s.backBtn} onClick={() => navigate('/companions')}>← back</button>
        <div style={s.title}>Companions</div>
        <div style={s.headerRight}>
          {isAdmin && (
            <button
              style={s.plusBtn}
              onClick={() => navigate('/companions/new')}
              title="new companion"
              aria-label="new companion"
            >
              <PlusIcon size={16} color="#0e0b0f" />
            </button>
          )}
        </div>
      </header>

      {/* BODY */}
      <div style={s.body}>
        <div style={s.sectionRow}>
          <div style={s.sectionTitle}>
            {companions.length} companion{companions.length === 1 ? '' : 's'}
          </div>
          <div style={s.sortWrap}>
            <button
              style={s.sortBtn(sortMode === 'activity')}
              onClick={() => changeSort('activity')}
              aria-pressed={sortMode === 'activity'}
            >
              recent
            </button>
            <button
              style={s.sortBtn(sortMode === 'alpha')}
              onClick={() => changeSort('alpha')}
              aria-pressed={sortMode === 'alpha'}
            >
              a–z
            </button>
          </div>
        </div>

        {loading ? null : sorted.length === 0 ? (
          <div style={s.emptyState}>
            <div style={s.emptyText}>no companions yet 🥀</div>
          </div>
        ) : (
          <div style={s.companionGrid}>
            {sorted.map((c, i) => {
              const highlight = sortMode === 'activity' && i === 0 && !!c.last_activity;
              return (
                <div
                  key={c.id}
                  className="r-card"
                  style={s.companionCard(highlight)}
                  role="button" tabIndex={0}
                  onClick={() => navigate(`/threads/${c.id}`)}
                  onKeyDown={(e) => e.key === 'Enter' && navigate(`/threads/${c.id}`)}
                >
                  {highlight && <div style={s.companionGlow} />}
                  <div style={s.companionAvatar}>
                    {c.avatar_url
                      ? <img src={c.avatar_url} alt={c.name} style={s.companionImg} />
                      : c.name?.[0] || 'C'
                    }
                  </div>
                  <div style={s.companionName}>{c.name}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
